import get from 'lodash/get';
import {getConfig} from '../config/portal';
import {getBoatsConstants} from '../constants/boats';
import {getActiveParamsLength} from './urlHelpers/boats';
import {logger} from './logger';

export const getPortalDefaultCountry = () => get(getConfig(), 'country');

const handleForceFilter = (filter, country) => {
  const portalFiltersConfig = get(getConfig(), 'pages.searchResults.facets', []);
  const filterConfig = portalFiltersConfig.find((config) => config.name === filter);
  if (
    filterConfig.forceDisabledForCountries &&
    filterConfig.forceDisabledForCountries.includes(country?.toUpperCase())
  ) {
    return false;
  }
  if (
    filterConfig.forceEnabledForCountries &&
    filterConfig.forceEnabledForCountries.includes(country?.toUpperCase())
  ) {
    return true;
  }
};

export const isFilterEnabledForPortal = (filter, context = null) => {
  const conf = context || getConfig();
  const portalFiltersConfig = conf?.pages?.searchResults?.facets || [];
  return portalFiltersConfig.some(config => config.name === filter);
};

export const portalHasCountryFilter = (context = null) => {
  return isFilterEnabledForPortal('country', context);
};

export const countryHasRegionFilter = (country) => {
  const filterConfig = get(getBoatsConstants().LOCATION_FILTERS_CONFIG, `${country?.toUpperCase()}.grouping`, false);
  return filterConfig && isFilterEnabledForPortal('region');
};

export const isRegionFilterEnabled = (selectedCountry) => {
  const forceFilter = handleForceFilter('region', selectedCountry);
  if (forceFilter !== undefined) {
    return forceFilter;
  }

  const country = portalHasCountryFilter() ? selectedCountry : getPortalDefaultCountry();
  return countryHasRegionFilter(country);
};

export const countryHasSubdivisionFilter = (country) => {
  const filterConfig = get(getBoatsConstants().LOCATION_FILTERS_CONFIG, `${country?.toUpperCase()}.subdivision`, false);
  return filterConfig && isFilterEnabledForPortal('subdivision');
};

export const isSubdivisionFilterEnabled = (selectedCountry, selectedRegion) => {
  const forceFilter = handleForceFilter('subdivision', selectedCountry);
  if (forceFilter !== undefined) {
    return forceFilter;
  }

  const country = portalHasCountryFilter() ? selectedCountry : getPortalDefaultCountry();
  const isRegionRequired = countryHasRegionFilter(country);
  const countryHasTheFilter = countryHasSubdivisionFilter(country);
  return isRegionRequired ? Boolean(selectedRegion) && countryHasTheFilter : countryHasTheFilter;
};

export const countryHasCityFilter = (country) => {
  const filterConfig = get(getBoatsConstants().LOCATION_FILTERS_CONFIG, `${country?.toUpperCase()}.city`, false);
  return filterConfig && isFilterEnabledForPortal('city');
};

export const isCityFilterEnabled = (selectedCountry, selectedSubdivision) => {
  const forceFilter = handleForceFilter('city', selectedCountry);
  if (forceFilter !== undefined) {
    return forceFilter;
  }

  const country = portalHasCountryFilter() ? selectedCountry : getPortalDefaultCountry();
  const isSubdivisionRequired = countryHasSubdivisionFilter(country);
  const countryHasTheFilter = countryHasCityFilter(country);
  return isSubdivisionRequired ? Boolean(selectedSubdivision) && countryHasTheFilter : countryHasTheFilter;
};

export const isFilteringOnlyByCity = (params) => {
  let {country, region} = params;
  if (isRegionFilterEnabled(country) && isSubdivisionFilterEnabled(country, region)) {
    return getActiveParamsLength(params) === 4;
  }
  if (isSubdivisionFilterEnabled(country, region)) {
    return getActiveParamsLength(params) === 3;
  }
  return getActiveParamsLength(params) === 2;
};

export const isFilteringOnlyBySubdivision = (params) => {
  let {country} = params;
  if (isRegionFilterEnabled(country)) {
    return getActiveParamsLength(params) === 3;
  }
  return getActiveParamsLength(params) === 2;
};

export const isFilteringOnlyByRegion = (params) => {
  return getActiveParamsLength(params) === 2;
};

export const isFilteringOnlyByCountry = (params) => {
  return getActiveParamsLength(params) === 1;
};

export const shouldForceCities = (lang, defaults = getBoatsConstants()) => {
  const {LOCATION_FILTERS_CONFIG} = defaults;
  const country = LOCATION_FILTERS_CONFIG[lang];
  return country &&
    !country.grouping &&
    !country.subdivision &&
    country.city &&
    country.forceCities;
};

/**
 * Get the appropriate "all subdivisions" message ID based on country
 * @param {string} chosenCountry - The selected country code
 * @param {object} messages - The translation messages object
 * @returns {object} - The message ID to use
 */
export const getAllSubdivisionsMessageId = (chosenCountry, messages) => {
  // Check for country-specific "all subdivisions" text
  if (chosenCountry === 'GB' && messages.allSubdivisionsGB) {
    return messages.allSubdivisionsGB;
  } else if (chosenCountry === 'US' && messages.allSubdivisionsUS) {
    return messages.allSubdivisionsUS;
  } else if (chosenCountry === 'FR' && messages.allSubdivisionsFR) {
    return messages.allSubdivisionsFR;
  } else if (chosenCountry === 'DE' && messages.allSubdivisionsDE) {
    return messages.allSubdivisionsDE;
  }
  // Fallback to default "all subdivisions" text
  return messages.allSubdivisions;
};

// Store the original default message
let originalDefaultMessage = null;

/**
 * Override the default 'moreSubdivisions' message with country-specific one if available
 * @param {string} chosenCountry - The selected country code
 * @param {object} translations - The translation messages object
 * @returns {object} - The translations object with updated moreSubdivisions
 */
export const setCountrySpecificMoreSubdivisionsMessage = (chosenCountry, translations) => {
  // Store the original default message the first time this function is called
  if (!originalDefaultMessage && translations.moreSubdivisions) {
    originalDefaultMessage = translations.moreSubdivisions;
  }
  // Always set it if originalDefaultMessage exists (which it should after first call)
  originalDefaultMessage && (translations.moreSubdivisions = originalDefaultMessage);
  // Override the default 'moreSubdivisions' message with country-specific one if available
  if (chosenCountry === 'GB' && translations.moreSubdivisionsGB) {
    translations.moreSubdivisions = translations.moreSubdivisionsGB;
  } else if (chosenCountry === 'US' && translations.moreSubdivisionsUS) {
    translations.moreSubdivisions = translations.moreSubdivisionsUS;
  } else if (chosenCountry === 'FR' && translations.moreSubdivisionsFR) {
    translations.moreSubdivisions = translations.moreSubdivisionsFR;
  } else if (chosenCountry === 'DE' && translations.moreSubdivisionsDE) {
    translations.moreSubdivisions = translations.moreSubdivisionsDE;
  }
  return translations;
};

/**
 * Get country-specific placeholder if available, otherwise use the default
 * @param {string} chosenCountry - The selected country code
 * @param {object} translations - The translation messages object
 * @param {object} intl - The intl object with formatMessage function
 * @returns {object} - The placeholder message ID to use
 */
export const getSubdivisionPlaceholderKey = (chosenCountry, translations, intl) => {
  // Try to get country-specific placeholder (e.g., app.search.filter.subdivision.placeholder.GB)
  const countrySpecificId = `app.search.filter.subdivision.placeholder.${chosenCountry}`;
  try {
    // Try to format the message to see if it exists
    const countrySpecificMessage = intl.formatMessage({ id: countrySpecificId });
    if (countrySpecificMessage && !countrySpecificMessage.startsWith('app.search.filter.subdivision.placeholder.')) {
      return { id: countrySpecificId };
    }
  } catch (e) {
    logger.error(e);
  }
  // Fallback to default placeholder
  return translations.subdivisionsPlaceholder;
};
